import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

export default function SplashScreen() {
  const navigate = useNavigate();
  const { loadAccounts, login } = useApp();
  const [visible, setVisible] = useState(false);
  const [username, setUsername] = useState(() => localStorage.getItem('mariani_last_user') || 'admin');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleEnter = async (e) => {
    e?.preventDefault();
    if (!username.trim()) { setError('Inserisci l\'utente'); return; }
    if (!password.trim()) { setError('Inserisci la password'); return; }
    setError('');
    setLoading(true);
    try {
      await login(username.trim().toLowerCase(), password);
      localStorage.setItem('mariani_last_user', username.trim().toLowerCase());
      const accs = await loadAccounts();
      if (!accs || accs.length === 0) {
        navigate('/settings');
      } else {
        navigate('/dashboard');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Credenziali errate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="splash-bg fixed inset-0 flex flex-col items-center justify-between overflow-hidden"
      data-testid="splash-screen"
      style={{ background: '#FAF7F4' }}
    >
      {/* Background texture */}
      <div className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `radial-gradient(ellipse at 50% 40%, rgba(226,114,91,0.12) 0%, transparent 65%)`
        }}
      />

      {/* Logo area */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-8"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
        }}
      >
        <img
          src="/logo.png"
          alt="Mariani Logo"
          className="w-auto max-w-xs md:max-w-sm lg:max-w-md"
          style={{ maxHeight: '300px', objectFit: 'contain' }}
          data-testid="splash-logo"
        />
        <div
          className="mt-6 text-center"
          style={{
            opacity: visible ? 1 : 0,
            transition: 'opacity 1s ease-out 0.4s',
          }}
        >
          <p
            className="text-sm uppercase tracking-widest"
            style={{ color: 'rgba(226,114,91,0.85)', fontFamily: 'Manrope, sans-serif', letterSpacing: '4px' }}
          >
            Mariani Inbox
          </p>
        </div>
      </div>

      {/* ENTRA button */}
      <div
        className="pb-16 flex flex-col items-center gap-4"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.8s ease-out 0.6s, transform 0.8s ease-out 0.6s',
        }}
      >
        <form onSubmit={handleEnter} className="flex flex-col items-center gap-3 w-64">
          {/* Selettore utente */}
          <div className="relative w-full">
            <select
              data-testid="splash-user-select"
              value={username}
              onChange={e => setUsername(e.target.value)}
              className="w-full px-4 py-3 pr-10 text-sm text-center rounded-xl border focus:outline-none transition-all appearance-none cursor-pointer font-medium"
              style={{
                borderColor: '#D9D2C8',
                background: 'white',
                color: '#4B4540',
                fontFamily: 'Manrope, sans-serif',
                letterSpacing: '2px',
                textTransform: 'uppercase',
              }}
              onFocus={e => e.target.style.borderColor = '#E2725B'}
              onBlur={e => e.target.style.borderColor = '#D9D2C8'}
            >
              <option value="admin">Admin</option>
              <option value="ufficio">Ufficio</option>
            </select>
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
              width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>

          <div className="relative w-full">
            <input
              data-testid="splash-password-input"
              type={showPwd ? 'text' : 'password'}
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              placeholder="Password"
              className="w-full px-4 py-3 pr-10 text-sm text-center rounded-xl border focus:outline-none transition-all"
              style={{
                borderColor: error ? '#E2725B' : '#D9D2C8',
                background: 'white',
                fontFamily: 'Manrope, sans-serif',
                letterSpacing: showPwd ? 'normal' : '3px',
              }}
              onFocus={e => e.target.style.borderColor = '#E2725B'}
              onBlur={e => e.target.style.borderColor = error ? '#E2725B' : '#D9D2C8'}
            />
            <button
              type="button"
              onClick={() => setShowPwd(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>

          {error && (
            <p className="text-xs text-center" style={{ color: '#E2725B' }}>{error}</p>
          )}

          <button
            data-testid="splash-enter-btn"
            type="submit"
            disabled={loading}
            className="w-full px-8 py-4 text-white font-semibold text-sm uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-2"
            style={{
              background: '#E2725B',
              fontFamily: 'Manrope, sans-serif',
              letterSpacing: '5px',
              borderRadius: '2px',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.8 : 1,
            }}
            onMouseEnter={e => !loading && (e.currentTarget.style.background = '#d4644d')}
            onMouseLeave={e => e.currentTarget.style.background = '#E2725B'}
          >
            {loading ? <><Loader2 size={14} className="animate-spin" /> Accesso...</> : 'ENTRA'}
          </button>
        </form>

        <p className="text-xs" style={{ color: 'rgba(0,0,0,0.3)', fontFamily: 'Manrope, sans-serif' }}>
          Mariani Inbox &mdash; Dal 1914
        </p>
      </div>
    </div>
  );
}
